import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Check, Xmark, Download } from 'iconoir-react';

interface GdprRequest {
  id: number;
  user: {
    id: number;
    name: string;
    email: string;
  };
  type: string;
  status: string;
  description: string | null;
  requested_at: string;
  processed_at: string | null;
}

const GdprManagement: React.FC = () => {
  const [requests, setRequests] = useState<GdprRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const response = await api.get('/admin/gdpr-requests');
      setRequests(response.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des demandes:', error);
    } finally {
      setLoading(false);
    }
  };

  const processRequest = async (requestId: number, status: string) => {
    const notes = window.prompt('Notes administratives (optionnel):');
    try {
      await api.put(`/admin/gdpr-requests/${requestId}/process`, {
        status,
        admin_notes: notes,
      });
      await loadRequests();
      alert('Demande traitée avec succès');
    } catch (error) {
      console.error('Erreur lors du traitement:', error);
      alert('Erreur lors du traitement de la demande');
    }
  };

  const exportUserData = async (userId: number) => {
    try {
      const response = await api.get(`/admin/users/${userId}/export-data`);
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `user_${userId}_data.json`;
      link.click();
      alert('Données exportées avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      alert('Erreur lors de l\'export des données');
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      access: 'Accès aux données',
      deletion: 'Suppression des données',
      portability: 'Portabilité des données',
      rectification: 'Rectification des données',
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status: string) => {
    const badges: { [key: string]: { label: string; color: string } } = {
      pending: { label: 'En attente', color: '#f39c12' },
      processing: { label: 'En cours', color: '#3498db' },
      completed: { label: 'Terminée', color: '#00bf7e' },
      rejected: { label: 'Rejetée', color: '#e74c3c' },
    };
    return badges[status] || { label: status, color: '#95a5a6' };
  };

  if (loading) return <div>Chargement...</div>;

  return (
    <div className="gdpr-management">
      <div className="section-header">
        <h2>Demandes RGPD</h2>
        <p>{requests.length} demande(s) au total</p>
      </div>

      <div className="gdpr-requests-list">
        {requests.length === 0 ? (
          <div className="empty-state">Aucune demande RGPD</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Utilisateur</th>
                <th>Type</th>
                <th>Statut</th>
                <th>Date de demande</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => {
                const statusBadge = getStatusBadge(request.status);
                return (
                  <tr key={request.id}>
                    <td>
                      <div>
                        <strong>{request.user.name}</strong>
                        <br />
                        <small>{request.user.email}</small>
                      </div>
                    </td>
                    <td>{getTypeLabel(request.type)}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: statusBadge.color }}
                      >
                        {statusBadge.label}
                      </span>
                    </td>
                    <td>{new Date(request.requested_at).toLocaleDateString('fr-FR')}</td>
                    <td>{request.description || '-'}</td>
                    <td>
                      {request.status === 'pending' && (
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => processRequest(request.id, 'completed')}
                            title="Approuver"
                          >
                            <Check width={16} height={16} strokeWidth={2} />
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => processRequest(request.id, 'rejected')}
                            title="Rejeter"
                          >
                            <Xmark width={16} height={16} strokeWidth={2} />
                          </button>
                          {request.type === 'portability' && (
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => exportUserData(request.user.id)}
                              title="Exporter les données"
                            >
                              <Download width={16} height={16} strokeWidth={2} />
                            </button>
                          )}
                        </div>
                      )}
                      {request.status === 'completed' && (
                        <span className="text-muted">Traitée</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default GdprManagement;
